import ContentStatusBadge from 'components/badge/ContentStatusBadge';
import ContentTypeBadge from 'components/badge/ContentTypeBadge';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { AppHelper } from 'helpers/appHelper';
import { ContentHelper } from 'helpers/contentHelper';
import { useApiState } from 'hooks/useApiState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';
import { ReportSearchItemType } from 'types/api/reportSearchItemType';
import WsModalReportDownload from './component/modal/WsModalReportDownload';
import WsReportSearchEmpty from './component/pages/report/WsReportSearchEmpty';

/**
 *　レポート画面
 */
export default function WsReportSearch(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { pageNo } = useParams();

  const [acctState] = useRecoilState(AcctState);

  const { postReportSearch } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [pageNoState, setPageNoState] = useState(1);
  const [contentsState, setContentsState] = useState<ReportSearchItemType[]>([]);
  const [hasNextState, setHasNextState] = useState(false);

  const [modalReportDownloadCd, setModalReportDownloadCd] = useState(null);
  const [modalReportDownloadContentsType, setModalReportDownloadContentsType] = useState(null);
  const [modalReportDownloadOpenState, setModalReportDownloadOpenState] = useState(false);

  const pageLimit = 10;
  const isExpiredPlan =
    acctState.company.plan.planTypeDiv === DivConst.items.planType.expiredTrial.value ||
    acctState.company.plan.planTypeDiv === DivConst.items.planType.expiredBasic.value;

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    // 認証済の場合は、会社登録画面に遷移
    (async () => {
      try {
        await _search(pageNo);
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　レポート出力ボタン押下時の処理
   */
  const onhModalReportDownloadOpen = (content: ReportSearchItemType) => {
    if (isExpiredPlan) {
      // プラン（利用期間切れ）の場合は処理しない
      return;
    }
    setModalReportDownloadCd(content.cd);
    setModalReportDownloadContentsType(content.contentsTypeDiv);
    setModalReportDownloadOpenState(true);
  };

  /**
   * ページングの前へボタン押下時の処理
   */
  const onPrev = async () => {
    if (pageNoState <= 1) {
      return;
    }
    if (pageNoState == 2) {
      navigate(`/ws/${acctState.company.cd}/report/search`);
    } else {
      navigate(`/ws/${acctState.company.cd}/report/search/${pageNoState - 1}`);
    }
  };

  /**
   * ページングの次へボタン押下時の処理
   */
  const onNext = async () => {
    if (!hasNextState) {
      return;
    }
    navigate(`/ws/${acctState.company.cd}/report/search/${pageNoState + 1}`);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * コンテンツ情報を検索します。
   */
  const _search = async (pageNo: string) => {
    let no = pageNo ? parseInt(pageNo) : 1;
    if (no < 1) no = 1;

    // ローディングの表示
    setProcessing(true);

    // コンテンツ検索APIの呼び出し
    const response = await postReportSearch({
      companyId: acctState.company.id,
      limit: pageLimit,
      offset: (no - 1) * pageLimit,
    });
    // 検索結果の設定
    setContentsState(response.contents);
    setPageNoState(no);
    setHasNextState(response.hasNext);

    // ローディングの停止
    setProcessing(false);
  };
  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-header">
        <div className="container">
          <div className="d-md-flex d-flex-header">
            <div className="flex-shrink-0 flex-left">
              <h2 className="flex-title">レポート</h2>
            </div>
          </div>
        </div>
      </section>

      {/* ローディング中の表示 */}
      <IndicatorScreen visible={processing} />

      {/* クイズ未登録の場合の表示*/}
      {initialized && pageNoState == 1 && AppHelper.isEmpty(contentsState) && (
        <WsReportSearchEmpty />
      )}

      {AppHelper.isNotEmpty(contentsState) && (
        <section className="section-report">
          <div className="container">
            <div className="box-common box-common-lg">
              <div className="table-responsive js-scrollable">
                <table className="table table-top-gray table-top-gray-list">
                  <thead>
                    <tr>
                      <th className="col-1">コンテンツタイトル</th>
                      <th className="col-2">
                        日付
                        <span>▲</span>
                      </th>
                      <th className="col-3">参加数</th>
                      <th className="col-4">リスト数</th>
                      <th className="col-5">獲得率</th>
                      <th className="col-5">完了率</th>
                      <th className="col-5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {contentsState.map(content => {
                      return (
                        <tr key={`ws-report-search-${content.id}`}>
                          <th>
                            <div className="d-flex d-flex-table">
                              <div className="flex-shrink-0 flex-img">
                                {/* クイズ画像の表示 */}
                                <img
                                  src={AppHelper.getContentImageUrl(content.imageKey)}
                                  className="img-fluid"
                                />
                              </div>
                              {/* クイズタイトルの表示 */}
                              <div className="flex-grow-1 flex-text justify-content-start">
                                <NavLink
                                  to={`/ws/${
                                    acctState.company.cd
                                  }/report/${ContentHelper.getContentsTypeStr(
                                    content.contentsTypeDiv
                                  )}/${content.cd}`}
                                  className="detail hover quiz-title mb-1"
                                >
                                  {content.title}
                                </NavLink>
                                <span>
                                  <ContentTypeBadge contentsTypeDiv={content.contentsTypeDiv} />
                                </span>
                              </div>
                            </div>
                          </th>
                          <td>
                            {/* 登録日時の表示 */}
                            <p className="date-time mb-1">
                              {AppHelper.formatTimeToDate(
                                content.publishedAt,
                                'yyyy年M月dd日 h:mm a'
                              )}
                            </p>
                            {/* ステータスの表示 */}
                            <ContentStatusBadge
                              statusDiv={content.statusDiv}
                              contentsTypeDiv={content.contentsTypeDiv}
                            />
                          </td>
                          <td>
                            {/* 参加数 */}
                            {AppHelper.formatNumberToCurrency(content.participatedCount)}
                            <p className="number-container">
                              {/* <span className="icon-ani">
                                    <img src="/img/icon/arrow-up.svg" alt="" />
                                  </span>
                                  <span className="text-blue number">1.3%</span> */}
                            </p>
                          </td>
                          <td>
                            {/* リスト数 */}
                            {AppHelper.formatNumberToCurrency(content.mailAddressCount)}
                            <p className="number-container">
                              {/* <span className="icon-ani">
                                    <img
                                      src="/img/icon/arrow-down.svg"
                                      alt=""
                                    />
                                  </span>
                                  <span className="text-pink number">4.3%</span> */}
                            </p>
                          </td>
                          <td>
                            {/* 獲得率 */}
                            {`${AppHelper.formatRateToPercent(
                              content.mailAddressAcquisitionRate
                            )}%`}

                            <p className="number-container">
                              {/* <span className="icon-ani">
                                    <img src="/img/icon/arrow-up.svg" alt="" />
                                  </span>
                                  <span className="text-blue number">1.3%</span> */}
                            </p>
                          </td>
                          <td>
                            {/* 完了率 */}
                            {`${AppHelper.formatRateToPercent(content.answeredRate)}%`}

                            <p className="number-container">
                              {/* <span className="icon-ani">
                                        <img
                                          src="/img/icon/arrow-up.svg"
                                          alt=""
                                        />
                                      </span>
                                      <span className="text-blue number">
                                        1.3%
                                      </span> */}
                            </p>
                          </td>
                          <td>
                            <div className="d-flex">
                              <div className="flex-grow-1 item-button">
                                <NavLink
                                  to={`/ws/${
                                    acctState.company.cd
                                  }/report/${ContentHelper.getContentsTypeStr(
                                    content.contentsTypeDiv
                                  )}/${content.cd}`}
                                  className="btn btn-common btn-common-w110 moderate-blue"
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  レポート詳細
                                </NavLink>
                                <a
                                  onClick={() => onhModalReportDownloadOpen(content)}
                                  className={
                                    isExpiredPlan
                                      ? 'btn btn-disabled btn-common-w110 outline-blue'
                                      : 'btn btn-common btn-common-w110 outline-blue'
                                  }
                                  style={{ whiteSpace: 'nowrap' }}
                                  data-tooltip-id="custom-tooltip"
                                  data-tooltip-content="プランの期限がきれました。プランのアップグレードをご検討ください。"
                                >
                                  レポート出力
                                </a>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ページングボタン */}
              <nav
                aria-label="Page navigation"
                className="navigation mt-4"
                style={{ position: 'static' }}
              >
                <ul className="pagination mb-0">
                  <li className="page-item">
                    <a
                      onClick={onPrev}
                      className={`page-link prev ${pageNoState <= 1 && `disabled`}`}
                      aria-label="Previous"
                    >
                      <span aria-hidden="true"></span>
                    </a>
                  </li>
                  <li className="page-item">
                    <a
                      onClick={onNext}
                      className={`page-link next ${!hasNextState && `disabled`}`}
                      aria-label="Next"
                    >
                      <span aria-hidden="true"></span>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </section>
      )}
      {/* レポートダウンロードモーダル */}
      <WsModalReportDownload
        open={modalReportDownloadOpenState}
        setOpen={setModalReportDownloadOpenState}
        cd={modalReportDownloadCd}
        contentsType={modalReportDownloadContentsType}
        reportDateFrom={null}
        reportDateTo={null}
      />
      {
        // プラン（利用期間切れ）の場合に表示する
        isExpiredPlan && <Tooltip id="custom-tooltip" place="top" variant="info" />
      }
    </>
  );
}
